// The New offer wizard (spec/dashboard.md section 7.3): one dialog that stages a whole offer campaign — the strings, the
// store item (picked or new), the offer with its art and limits, the condition leaves and the game event that attaches it
// (or an edit of an existing event's attachment) — all linked by temp ids, so Save All commits them together. The When / Who
// part (ScheduleFields) is shared with the offers workspace's "Schedule this offer…" and "Attach to an existing event…"
// dialogs, which do the same for an offer that exists already.
import { useMemo, useState, type ReactNode } from 'react';
import type { Document, Json, Parameter, Template } from '../../api/types';
import { useContent, type ContentCatalog } from '../../content/ContentContext';
import { baseOf } from '../../content/actions';
import { CONDITION_NS } from '../../content/conditions/model';
import { AssetDropField } from '../../content/widgets/AssetDropField';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { PriceField, RewardField } from '../../content/widgets/economy';
import { freshDocument, heirsOf, useWidgetEnv } from '../../content/widgets/context';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { AlertCircle, AlertTriangle } from '../../ui/icons';
import { NumberInput, Select, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { Tabs } from '../../ui/Tabs';
import { useToasts } from '../../ui/Toast';
import { COMMON_DURATION_PRESETS, DateTimeInput, DurationInput } from '../../ui/values';
import type { Workspace } from '../../workspace/Workspace';
import { EVENT, OFFER, STORE_ITEM, stage } from './cards';
import './liveops.css';

export const SEGMENT_OPTION = 'SmartObjects.SegmentOption';
const STRING_GROUP = 'LIVEOPS';
const TEMPLATES_KEY = (gameId: string) => `hue.offer-wizard.templates:${gameId}`;

type Env = { workspace: Workspace; catalog: ContentCatalog };

// ---- strings ------------------------------------------------------------------------------------------------------------

/** `Halloween Deal` → `HALLOWEEN_DEAL`; empty → `OFFER`. */
export function keyOf(name: string): string {
  return name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '') || 'OFFER';
}

/** The key, or `KEY_2`, `KEY_3`… when the group already has it (on the server or staged). */
export function uniqueKey(env: Env, group: string, key: string): string {
  const taken = new Set<string>();
  for (const s of env.catalog.stringList) if (s.group === group) taken.add(s.key);
  for (const d of env.workspace.all) {
    if (d.kind !== 'string' || d.status === 'deleted') continue;
    const s = d.current as { group?: string; key?: string };
    if (s.group === group && s.key) taken.add(s.key);
  }
  if (!taken.has(key)) return key;
  for (let n = 2; ; n++) if (!taken.has(`${key}_${n}`)) return `${key}_${n}`;
}

/** Stages a localization string of the LIVEOPS group with the default-language text and answers its temp id. */
export function stageString(env: Env, key: string, text: string): string {
  const k = uniqueKey(env, STRING_GROUP, key);
  return env.workspace.create('string', { id: '', group: STRING_GROUP, key: k, description: null, export: true, values: { [env.catalog.defaultLanguage]: text.trim() } } as unknown as Json, { label: `${STRING_GROUP}/${k}`, template: 'Localization' });
}

// ---- the schedule (When / Who) --------------------------------------------------------------------------------------------

export interface ScheduleState {
  when: 'always' | 'range' | 'attach';
  start: number;
  finish: number;
  repeat: number;
  /** the existing event to attach to (when === 'attach') */
  eventId: string | null;
  who: 'everyone' | 'segment';
  segmentId: string | null;
}

export function defaultSchedule(now = Math.floor(Date.now() / 1000)): ScheduleState {
  const start = now - (now % 3600) + 3600;
  return { when: 'always', start, finish: start + 7 * 86400, repeat: 0, eventId: null, who: 'everyone', segmentId: null };
}

export function scheduleValid(s: ScheduleState): boolean {
  if (s.when === 'range') return s.finish > s.start;
  if (s.when === 'attach') return typeof s.eventId === 'string' && s.eventId.length > 0;
  return true;
}

/** A reference parameter built from an existing one (the shape of a Parameter stays the catalog's). */
function refParam(from: Parameter, name: string, displayName: string, template: string): Parameter {
  return { ...from, name, displayName, type: 'document', template, of: null, description: null };
}

function anyParam(catalog: ContentCatalog): Parameter | undefined {
  return catalog.templates.get(OFFER)?.effectiveParameters.find((p) => p.name === 'storeItem') ?? catalog.templates.get(EVENT)?.effectiveParameters.find((p) => p.name === 'script');
}

function data(doc: Document | { data?: unknown } | undefined): Record<string, Json> {
  return ((doc?.data ?? {}) as Record<string, Json>) ?? {};
}

/** The When and Who sections; Who is hidden when the choice is to attach to an existing event (its condition is its own). */
export function ScheduleFields({ value, onChange, allowAttach = true, attachOnly = false, offerLabel = 'the new offer' }: { value: ScheduleState; onChange: (next: ScheduleState) => void; allowAttach?: boolean; /** only the event picker (the "Attach to an existing event…" dialog) */ attachOnly?: boolean; offerLabel?: string }) {
  const catalog = useContent();
  const { offsets } = useGame();
  const base = anyParam(catalog);
  const hasSegments = catalog.templates.has(SEGMENT_OPTION) && catalog.templates.has(`${CONDITION_NS}SegmentCondition`);
  const set = (patch: Partial<ScheduleState>) => onChange({ ...value, ...patch });
  const picked = value.when === 'attach' && value.eventId ? catalog.resolve(value.eventId) : undefined;
  const attached = picked ? data(picked).script : null;
  const attachedDoc = typeof attached === 'string' ? catalog.resolve(attached) : undefined;
  return (
    <>
      {!attachOnly && (
        <>
          <div className="section-head">
            <h3>When</h3>
          </div>
          <Tabs
            segmented
            ariaLabel="When it runs"
            value={value.when}
            onChange={(k) => set({ when: k as ScheduleState['when'] })}
            items={[{ key: 'always', label: 'Always on' }, { key: 'range', label: 'Date range' }, ...(allowAttach ? [{ key: 'attach', label: 'Attach to an existing event' }] : [])]}
          />
        </>
      )}
      {value.when === 'always' && <p className="lo-note">The event runs whenever its other conditions hold; no time window.</p>}
      {value.when === 'range' && (
        <>
          <div className="form-grid">
            <Field label="Starts">
              <DateTimeInput value={value.start} onCommit={(s) => s !== null && set({ start: s })} ctx={{ offsets }} aria-label="Starts" />
            </Field>
            <Field label="Ends">
              <DateTimeInput value={value.finish} onCommit={(s) => s !== null && set({ finish: s })} ctx={{ offsets }} aria-label="Ends" />
            </Field>
            <Field label="Repeat every" help="0 = never">
              <DurationInput value={value.repeat} onCommit={(repeat) => set({ repeat })} zeroMeans="never" aria-label="Repeat every" />
            </Field>
          </div>
          {value.finish <= value.start && (
            <p className="lo-note danger" role="alert">
              <AlertCircle aria-hidden="true" /> The range is empty.
            </p>
          )}
          {value.repeat > 0 && value.repeat <= value.finish - value.start && (
            <p className="lo-note warning">
              <AlertTriangle aria-hidden="true" /> The repeat must be longer than the range.
            </p>
          )}
        </>
      )}
      {value.when === 'attach' && (
        <div className="form-grid">
          <Field label="Event" required help="The event's own condition decides when it runs" className="span-2">
            {base ? <FieldWidget param={refParam(base, 'event', 'Event', EVENT)} value={value.eventId} onCommit={(v) => set({ eventId: typeof v === 'string' ? v : null })} /> : <span className="muted">No GameEvent template on this branch.</span>}
          </Field>
          {picked && typeof attached === 'string' && (
            <p className="lo-note warning span-2">
              <AlertTriangle aria-hidden="true" /> This event attaches {attachedDoc ? catalog.label(attachedDoc) : 'another document'}; it will attach {offerLabel} instead.
            </p>
          )}
        </div>
      )}
      {value.when !== 'attach' && hasSegments && (
        <>
          <div className="section-head">
            <h3>Who</h3>
          </div>
          <Tabs segmented ariaLabel="Who sees it" value={value.who} onChange={(k) => set({ who: k as ScheduleState['who'] })} items={[{ key: 'everyone', label: 'Everyone' }, { key: 'segment', label: 'In a segment' }]} />
          {value.who === 'segment' && (
            <div className="form-grid">
              <Field label="Segment" required help="Only players in this segment see the offer; more conditions can be added on the event afterwards" className="span-2">
                {base ? <FieldWidget param={refParam(base, 'segment', 'Segment', SEGMENT_OPTION)} value={value.segmentId} onCommit={(v) => set({ segmentId: typeof v === 'string' ? v : null })} /> : null}
              </Field>
            </div>
          )}
        </>
      )}
    </>
  );
}

/**
 * Stages what the schedule means for `offerId`: a new event (with the TimeRage / SegmentCondition leaves under an And root,
 * or no condition) attaching the offer, or the picked event's attachment edited to it. Answers the event's id.
 */
export function stageSchedule(env: Env, s: ScheduleState, offerId: string, nameStringId: Json, eventTemplate: string): string | null {
  const { workspace, catalog } = env;
  if (s.when === 'attach') {
    if (!s.eventId) return null;
    const current = catalog.resolve(s.eventId) as Document | undefined;
    const server = catalog.resolver.get(s.eventId) as Document | undefined;
    const label = current ? catalog.label(current) : s.eventId.slice(0, 8);
    workspace.edit('document', s.eventId, 'data.script', offerId, { base: server ? baseOf(server) : undefined, label, template: current?.template ?? EVENT });
    return s.eventId;
  }
  const leaves: string[] = [];
  if (s.when === 'range') leaves.push(stage(workspace, catalog, `${CONDITION_NS}TimeRage`, { start: s.start, finish: s.finish, repeat: s.repeat }));
  if (s.who === 'segment' && s.segmentId) leaves.push(stage(workspace, catalog, `${CONDITION_NS}SegmentCondition`, { segment: s.segmentId }));
  const root = leaves.length > 0 ? stage(workspace, catalog, `${CONDITION_NS}And`, { conditions: leaves }) : null;
  return stage(workspace, catalog, eventTemplate, { name: nameStringId, condition: root, script: offerId, finishType: 0 });
}

// ---- the templates ------------------------------------------------------------------------------------------------------

interface TemplateChoice {
  offer: string;
  event: string;
  storeItem: string;
}

function concreteFamily(catalog: ContentCatalog, root: string): Template[] {
  const base = catalog.templates.get(root);
  return (base ? [base, ...heirsOf(catalog, root)] : []).filter((t) => !t.flags.includes('abstract'));
}

function loadChoice(gameId: string, catalog: ContentCatalog): TemplateChoice {
  const fallback = { offer: OFFER, event: EVENT, storeItem: STORE_ITEM };
  try {
    const raw = localStorage.getItem(TEMPLATES_KEY(gameId));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<TemplateChoice>;
    const ok = (name: string | undefined, root: string) => (name && catalog.templates.has(name) ? name : root);
    return { offer: ok(parsed.offer, OFFER), event: ok(parsed.event, EVENT), storeItem: ok(parsed.storeItem, STORE_ITEM) };
  } catch {
    return fallback;
  }
}

function saveChoice(gameId: string, choice: TemplateChoice): void {
  try {
    localStorage.setItem(TEMPLATES_KEY(gameId), JSON.stringify(choice));
  } catch {
    // the choice then lasts for the page
  }
}

const FREE_PRICE: Json = { type: 0, product: null, items: [], isVirtual: false, ads: 0 };
const NO_REWARD: Json = { items: [] };

function TemplateSelect({ label, value, options, onChange }: { label: string; value: string; options: Template[]; onChange: (name: string) => void }) {
  return (
    <Field label={label}>
      <Select size="s" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        {options.map((t) => (
          <option key={t.name} value={t.name}>
            {t.displayName ?? t.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function Head({ children }: { children: ReactNode }) {
  return (
    <div className="section-head">
      <h3>{children}</h3>
    </div>
  );
}

// ---- the wizard -----------------------------------------------------------------------------------------------------------

export interface OfferWizardProps {
  onClose: () => void;
  onCreated?: (ids: { offerId: string; eventId: string | null }) => void;
  /** open the editor of the event the offer runs in once staged (the events page and the calendar) */
  openEvent?: boolean;
}

export function OfferWizard({ onClose, onCreated, openEvent }: OfferWizardProps) {
  const { workspace, host, gameId } = useWidgetEnv();
  const catalog = useContent();
  const toasts = useToasts();
  const env: Env = { workspace, catalog };
  const [choice, setChoiceState] = useState<TemplateChoice>(() => loadChoice(gameId, catalog));
  const setChoice = (patch: Partial<TemplateChoice>) => {
    const next = { ...choice, ...patch };
    setChoiceState(next);
    saveChoice(gameId, next);
  };
  const offerDef = catalog.templates.get(choice.offer) ?? catalog.templates.get(OFFER);
  const storeDef = catalog.templates.get(choice.storeItem) ?? catalog.templates.get(STORE_ITEM);
  const offerTemplates = useMemo(() => concreteFamily(catalog, OFFER), [catalog]);
  const eventTemplates = useMemo(() => concreteFamily(catalog, EVENT), [catalog]);
  const storeTemplates = useMemo(() => concreteFamily(catalog, STORE_ITEM), [catalog]);
  const offerDefaults = useMemo(() => (offerDef ? data(freshDocument(offerDef) as { data?: unknown }) : {}), [offerDef]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sells, setSells] = useState<'pick' | 'new'>('pick');
  const [storeItem, setStoreItem] = useState<Json>(null);
  const [price, setPrice] = useState<Json>(FREE_PRICE);
  const [reward, setReward] = useState<Json>(NO_REWARD);
  const [storeSprite, setStoreSprite] = useState<Json>(null);
  const [schedule, setSchedule] = useState<ScheduleState>(() => defaultSchedule());
  const [assets, setAssets] = useState<Record<string, Json>>({});
  const [look, setLook] = useState<Record<string, Json>>({});
  const [duration, setDuration] = useState(10800);
  const [limit, setLimit] = useState(1);
  const [cooldown, setCooldown] = useState(0);

  const params = offerDef?.effectiveParameters ?? [];
  const assetParams = params.filter((p) => p.type === 'asset');
  const placementParam = params.find((p) => p.name === 'unnyPlacement');
  const priorityParam = params.find((p) => p.name === 'unnyPriority');
  const storeSpriteParam = storeDef?.effectiveParameters.find((p) => p.name === 'sprite' && p.type === 'asset');
  const priceParam = storeDef?.effectiveParameters.find((p) => p.name === 'price');
  const rewardParam = storeDef?.effectiveParameters.find((p) => p.name === 'reward');
  const storeItemParam = params.find((p) => p.name === 'storeItem');
  const lookValue = (p: Parameter) => (p.name in look ? look[p.name] : p.name === 'unnyPriority' ? (offerDefaults.unnyPriority ?? 100) : (offerDefaults[p.name] ?? null));

  const canCreate = !!offerDef && name.trim().length > 0 && (sells === 'pick' ? typeof storeItem === 'string' : !!storeDef) && scheduleValid(schedule);

  const create = () => {
    if (!offerDef || !storeDef) return;
    try {
      const key = keyOf(name);
      const localized = (def: Template | undefined, param: string) => def?.effectiveParameters.find((p) => p.name === param)?.type === 'localized_string';
      const nameId = stageString(env, `${key}_NAME`, name);
      const descId = description.trim() ? stageString(env, `${key}_DESCRIPTION`, description) : null;
      const storeItemId = sells === 'pick' ? (storeItem as string) : stage(workspace, catalog, storeDef.name, { name: localized(storeDef, 'name') ? nameId : name.trim(), price, reward, sprite: storeSprite });
      const offerData: Record<string, Json> = {
        name: localized(offerDef, 'name') ? nameId : name.trim(),
        storeItem: storeItemId,
        duration,
        limit,
        cooldown,
        wait: false,
        ...(priorityParam ? { unnyPriority: lookValue(priorityParam) } : {}),
        ...(placementParam ? { unnyPlacement: lookValue(placementParam) } : {}),
        ...assets,
      };
      if (descId) offerData.description = descId;
      const offerId = stage(workspace, catalog, offerDef.name, offerData);
      const eventId = stageSchedule(env, schedule, offerId, localized(catalog.templates.get(choice.event), 'name') ? nameId : name.trim(), choice.event);
      const attachedTo = schedule.when === 'attach' && eventId ? catalog.resolve(eventId) : undefined;
      toasts.success(attachedTo ? `Offer and strings staged and attached to ${catalog.label(attachedTo)}; Save All commits them together.` : 'Offer, event and strings staged; Save All commits them together.');
      onClose();
      onCreated?.({ offerId, eventId });
      if (openEvent && eventId) void host?.open(eventId);
    } catch (e) {
      toasts.error(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Modal
      title="New offer"
      open
      onClose={onClose}
      size="l"
      className="offer-wizard"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={create} disabled={!canCreate}>
            Create (staged)
          </Button>
        </>
      }
    >
      {!offerDef ? (
        <p className="muted">No GameOffer template on this branch.</p>
      ) : (
        <div className="lo-wizard" data-testid="offer-wizard">
          <Head>What</Head>
          <div className="form-grid">
            <Field label="Name" required help={`Shown in the game; staged as the localization string ${STRING_GROUP}/${keyOf(name)}_NAME (${catalog.defaultLanguage})`}>
              <TextInput value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" autoFocus />
            </Field>
            <Field label="Description" help="Optional; staged as a string next to the name">
              <TextInput value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Description" />
            </Field>
          </div>

          <Head>Sells</Head>
          <Tabs segmented ariaLabel="What it sells" value={sells} onChange={(k) => setSells(k as 'pick' | 'new')} items={[{ key: 'pick', label: 'Pick a store item' }, { key: 'new', label: 'New store item' }]} />
          {sells === 'pick' ? (
            <div className="form-grid">
              <Field label="Store item" required help="What the offer sells: its price and reward" className="span-2">
                {storeItemParam ? <FieldWidget param={{ ...storeItemParam, displayName: 'Store item' }} value={storeItem} onCommit={setStoreItem} /> : <span className="muted">No StoreItem template on this branch.</span>}
              </Field>
            </div>
          ) : (
            <div className="form-grid">
              <Field label="Price" help="Hard = a product, Soft = items, Ads = watched ads" className="span-2">
                {priceParam ? <PriceField param={{ ...priceParam, displayName: 'Price' }} value={price} onCommit={setPrice} /> : <span className="muted">No price parameter on the store item template.</span>}
              </Field>
              <Field label="Reward" help="What the player gets" className="span-2">
                {rewardParam ? <RewardField param={{ ...rewardParam, displayName: 'Reward' }} value={reward} onCommit={setReward} /> : null}
              </Field>
              {storeSpriteParam && (
                <Field label="Store item sprite" help="Optional">
                  <AssetDropField param={{ ...storeSpriteParam, displayName: 'Store item sprite' }} value={storeSprite} onCommit={setStoreSprite} size="s" />
                </Field>
              )}
            </div>
          )}

          <ScheduleFields value={schedule} onChange={setSchedule} />

          <Head>Look</Head>
          {assetParams.length > 0 && (
            <div className="art-grid">
              {assetParams.map((p) => (
                <Field key={p.name} label={p.displayName ?? p.name}>
                  <AssetDropField param={p} value={assets[p.name] ?? null} onCommit={(v) => setAssets((a) => ({ ...a, [p.name]: v }))} />
                </Field>
              ))}
            </div>
          )}
          <div className="form-grid">
            {placementParam && (
              <Field label={placementParam.displayName ?? 'Placement'}>
                <FieldWidget param={placementParam} value={lookValue(placementParam)} onCommit={(v) => setLook((l) => ({ ...l, [placementParam.name]: v }))} />
              </Field>
            )}
            {priorityParam && (
              <Field label={priorityParam.displayName ?? 'Priority'} help="Higher shows first when several offers compete">
                <FieldWidget param={priorityParam} value={lookValue(priorityParam)} onCommit={(v) => setLook((l) => ({ ...l, [priorityParam.name]: v }))} />
              </Field>
            )}
          </div>

          <Head>Limits</Head>
          <div className="form-grid">
            <Field label="Offer duration" help="0 = ends with the event">
              <DurationInput value={duration} onCommit={setDuration} zeroMeans="ends with the event" presets={COMMON_DURATION_PRESETS} aria-label="Offer duration" />
            </Field>
            <Field label="Purchase limit" help="0 = unlimited">
              <NumberInput kind="int" value={limit} onCommit={(v) => setLimit(Number(v ?? 0))} aria-label="Purchase limit" />
            </Field>
            <Field label="Cooldown" help="Before the offer can show again; 0 = no cooldown">
              <DurationInput value={cooldown} onCommit={setCooldown} zeroMeans="no cooldown" presets={COMMON_DURATION_PRESETS} aria-label="Cooldown" />
            </Field>
          </div>

          <details className="lo-advanced">
            <summary>Advanced</summary>
            <div className="form-grid">
              <TemplateSelect label="Offer template" value={choice.offer} options={offerTemplates} onChange={(offer) => setChoice({ offer })} />
              <TemplateSelect label="Event template" value={choice.event} options={eventTemplates} onChange={(event) => setChoice({ event })} />
              {sells === 'new' && <TemplateSelect label="Store item template" value={choice.storeItem} options={storeTemplates} onChange={(storeItem) => setChoice({ storeItem })} />}
            </div>
          </details>
        </div>
      )}
    </Modal>
  );
}

// ---- the offers workspace's dialogs for an existing offer -----------------------------------------------------------------

/** "Schedule this offer…": a new event (Always on / Date range, Everyone / In a segment) that attaches the offer. */
export function ScheduleDialog({ offer, onClose, onDone }: { offer: Document; onClose: () => void; onDone?: (eventId: string) => void }) {
  const { workspace } = useWidgetEnv();
  const catalog = useContent();
  const toasts = useToasts();
  const [schedule, setSchedule] = useState<ScheduleState>(() => defaultSchedule());
  const eventTemplates = useMemo(() => concreteFamily(catalog, EVENT), [catalog]);
  const [eventTemplate, setEventTemplate] = useState(EVENT);
  const create = () => {
    try {
      const eventId = stageSchedule({ workspace, catalog }, schedule, offer.id, data(offer).name ?? null, eventTemplate);
      toasts.success('Event staged and attached; Save All commits it.');
      onClose();
      if (eventId) onDone?.(eventId);
    } catch (e) {
      toasts.error(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <Modal
      title="Schedule this offer"
      open
      onClose={onClose}
      size="m"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={create} disabled={!scheduleValid(schedule)}>
            Create event (staged)
          </Button>
        </>
      }
    >
      <div className="lo-wizard" data-testid="schedule-dialog">
        <p className="lo-note">A game event activates the offer; it takes the offer's name.</p>
        <ScheduleFields value={schedule} onChange={setSchedule} allowAttach={false} offerLabel={catalog.label(offer)} />
        {eventTemplates.length > 1 && (
          <details className="lo-advanced">
            <summary>Advanced</summary>
            <div className="form-grid">
              <TemplateSelect label="Event template" value={eventTemplate} options={eventTemplates} onChange={setEventTemplate} />
            </div>
          </details>
        )}
      </div>
    </Modal>
  );
}

/** "Attach to an existing event…": the picked event's attachment becomes this offer (a staged edit of that event). */
export function AttachEventDialog({ offer, onClose, onDone }: { offer: Document; onClose: () => void; onDone?: (eventId: string) => void }) {
  const { workspace } = useWidgetEnv();
  const catalog = useContent();
  const toasts = useToasts();
  const [schedule, setSchedule] = useState<ScheduleState>(() => ({ ...defaultSchedule(), when: 'attach' }));
  const attach = () => {
    try {
      const eventId = stageSchedule({ workspace, catalog }, schedule, offer.id, data(offer).name ?? null, EVENT);
      const event = eventId ? catalog.resolve(eventId) : undefined;
      toasts.success(`Attached to ${event ? catalog.label(event) : 'the event'} (staged); Save All commits it.`);
      onClose();
      if (eventId) onDone?.(eventId);
    } catch (e) {
      toasts.error(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <Modal
      title="Attach to an existing event"
      open
      onClose={onClose}
      size="m"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={attach} disabled={!scheduleValid(schedule)}>
            Attach (staged)
          </Button>
        </>
      }
    >
      <div className="lo-wizard" data-testid="attach-dialog">
        <ScheduleFields value={schedule} onChange={setSchedule} attachOnly offerLabel={catalog.label(offer)} />
      </div>
    </Modal>
  );
}
